import { useState } from 'react';
import type { FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  REPORT_REASON_MAX_CHARS,
  blockContact,
  fetchContacts,
  removeContact,
  reportContact,
  sendContactInvite,
  unblockContact,
} from '../lib/api';
import type {
  ApiError,
  Contact,
  ContactState,
  SendContactInviteResponse,
} from '../lib/api';
import { EmptyState } from './EmptyState';
import { Skeleton } from './Skeleton';

const CONTACT_STATE_LABEL: Record<ContactState, string> = {
  active: 'Active',
  blocked: 'Blocked',
};

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

export function ContactsSection() {
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['contacts', 'all'],
    queryFn: () => fetchContacts('all'),
  });

  const [username, setUsername] = useState('');
  const [sentInvite, setSentInvite] =
    useState<SendContactInviteResponse | null>(null);

  const inviteMutation = useMutation({
    mutationFn: sendContactInvite,
    onSuccess: (resp) => {
      setSentInvite(resp);
      setUsername('');
    },
  });

  function handleInvite(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const target = username.trim();
    if (!target) return;
    inviteMutation.mutate({ username: target });
  }

  if (isLoading) {
    return (
      <div className="settings-section">
        <h3>Contacts</h3>
        <Skeleton
          className="settings-section-loading"
          ariaLabel="Loading contacts"
          rows={3}
          variant="list"
        />
      </div>
    );
  }

  if (isError) {
    const apiError = error as unknown as ApiError | undefined;
    return (
      <div className="settings-section">
        <h3>Contacts</h3>
        <div className="login-form-error" role="alert">
          {apiError?.status === 401
            ? 'Sign in to view your contacts.'
            : "We couldn't load your contacts. Try refreshing the page."}
        </div>
      </div>
    );
  }

  const contacts = data?.contacts ?? [];
  const inviteApiErr = inviteMutation.error as ApiError | null;

  return (
    <div className="settings-section">
      <h3>Contacts</h3>
      <p className="settings-section-help">
        Manage human contacts and invite new collaborators by Tizok username.
      </p>

      <form
        className="settings-live-form"
        aria-label="Invite contact"
        onSubmit={handleInvite}
      >
        <label className="settings-live-field" htmlFor="contact-username">
          <span>Contact username</span>
          <input
            id="contact-username"
            type="text"
            value={username}
            onChange={(e) => {
              setUsername(e.target.value);
              inviteMutation.reset();
              setSentInvite(null);
            }}
            placeholder="e.g. alice"
            aria-describedby="contact-username-hint"
            disabled={inviteMutation.isPending}
            required
          />
        </label>
        <p id="contact-username-hint" className="settings-hint">
          Or a full address, like @alice:tizok.ai.
        </p>

        {inviteApiErr && (
          <p className="settings-error" role="alert">
            {inviteApiErr.message}
          </p>
        )}

        {sentInvite && (
          <p className="settings-success" role="status" aria-live="polite">
            Invite sent to{' '}
            {sentInvite.invite.display_name ?? sentInvite.invite.user_id}.
          </p>
        )}

        <div className="settings-actions">
          <button
            type="submit"
            className="settings-action-primary"
            disabled={!username.trim() || inviteMutation.isPending}
          >
            {inviteMutation.isPending && (
              <span className="button-spinner" aria-hidden="true" />
            )}
            {inviteMutation.isPending ? 'Sending…' : 'Send invite'}
          </button>
        </div>
      </form>

      {contacts.length === 0 ? (
        <EmptyState
          className="settings-empty-state contacts-empty-state"
          icon="👥"
          title="No contacts yet"
          description="Send an invite above or from the CLI to start collaborating."
          action={
            <code className="settings-cli-hint-code">
              tizok invite &lt;username&gt;
            </code>
          }
        />
      ) : (
        <ul className="contacts-list" aria-label="Contacts">
          {contacts.map((contact) => (
            <ContactRow key={contact.user_id} contact={contact} />
          ))}
        </ul>
      )}
    </div>
  );
}

function ContactRow({ contact }: { contact: Contact }) {
  const queryClient = useQueryClient();
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [reporting, setReporting] = useState(false);
  const [reportReason, setReportReason] = useState('');
  const [reported, setReported] = useState(false);

  const removeMutation = useMutation({
    mutationFn: () => removeContact(contact.user_id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contacts'] });
      setConfirmRemove(false);
    },
  });

  const toggleBlockMutation = useMutation({
    mutationFn: () =>
      contact.state === 'blocked'
        ? unblockContact(contact.user_id)
        : blockContact(contact.user_id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['contacts'] }),
  });

  const reportMutation = useMutation({
    mutationFn: () =>
      reportContact(contact.user_id, reportReason.trim() || undefined),
    onSuccess: () => {
      setReporting(false);
      setReportReason('');
      setReported(true);
    },
  });

  const targetName = contact.display_name ?? contact.user_id;
  const stateClass = `contacts-row-state contacts-row-state-${contact.state}`;
  const isBlocked = contact.state === 'blocked';
  const busy =
    removeMutation.isPending ||
    toggleBlockMutation.isPending ||
    reportMutation.isPending;
  const reportApiErr = reportMutation.error as ApiError | null;

  return (
    <li className="contacts-row">
      <div className="contacts-row-main">
        <div className="contacts-row-name">{targetName}</div>
        <div className="contacts-row-meta">
          <span className={stateClass}>{CONTACT_STATE_LABEL[contact.state]}</span>
          <span className="contacts-row-meta-sep" aria-hidden="true">
            ·
          </span>
          <code className="settings-cli-hint-code">{contact.user_id}</code>
          <span className="contacts-row-meta-sep" aria-hidden="true">
            ·
          </span>
          <span>{contact.mutual_spaces} mutual spaces</span>
          <span className="contacts-row-meta-sep" aria-hidden="true">
            ·
          </span>
          <span>Connected {formatDate(contact.connected_at)}</span>
          {contact.last_dm_at && (
            <>
              <span className="contacts-row-meta-sep" aria-hidden="true">
                ·
              </span>
              <span>Last DM {formatDate(contact.last_dm_at)}</span>
            </>
          )}
        </div>
      </div>
      <div className="contacts-row-actions">
        <button
          type="button"
          className="settings-action-secondary"
          onClick={() => toggleBlockMutation.mutate()}
          disabled={busy}
        >
          {toggleBlockMutation.isPending && (
            <span className="button-spinner" aria-hidden="true" />
          )}
          {isBlocked ? 'Unblock' : 'Block'}
        </button>
        {confirmRemove ? (
          <span
            className="contacts-confirm-remove"
            role="group"
            aria-label="Confirm removal"
          >
            <span>Remove contact?</span>
            <button
              type="button"
              className="settings-action-danger"
              onClick={() => removeMutation.mutate()}
              disabled={removeMutation.isPending}
            >
              {removeMutation.isPending ? 'Removing…' : 'Confirm'}
            </button>
            <button
              type="button"
              className="settings-action-secondary"
              onClick={() => setConfirmRemove(false)}
              disabled={removeMutation.isPending}
            >
              Cancel
            </button>
          </span>
        ) : (
          <button
            type="button"
            className="settings-action-secondary"
            onClick={() => setConfirmRemove(true)}
            disabled={busy}
          >
            Remove
          </button>
        )}
        {!reported && !reporting && (
          <button
            type="button"
            className="settings-action-secondary"
            onClick={() => setReporting(true)}
            disabled={busy}
            aria-label={`Report ${targetName}`}
          >
            Report
          </button>
        )}
      </div>

      {reporting && (
        <form
          className="contacts-report-form"
          aria-label={`Report ${targetName}`}
          onSubmit={(e) => {
            e.preventDefault();
            reportMutation.mutate();
          }}
        >
          <p className="contacts-report-intro">
            Report this user to our safety team. This is a one-way report — they
            won't be notified. If safety is urgent, block them too.
          </p>
          <label
            htmlFor={`report-reason-${contact.user_id}`}
            className="spaces-create-label"
          >
            Reason{' '}
            <span className="spaces-create-optional">(optional)</span>
          </label>
          <textarea
            id={`report-reason-${contact.user_id}`}
            className="contacts-report-reason"
            value={reportReason}
            onChange={(e) => setReportReason(e.target.value)}
            maxLength={REPORT_REASON_MAX_CHARS}
            rows={3}
            placeholder="What happened? Add any context for the safety reviewer."
            disabled={reportMutation.isPending}
          />
          {reportApiErr && (
            <p className="settings-error" role="alert">
              {reportApiErr.message}
            </p>
          )}
          <div className="settings-actions">
            <button
              type="submit"
              className="settings-action-danger"
              disabled={reportMutation.isPending}
            >
              {reportMutation.isPending && (
                <span className="button-spinner" aria-hidden="true" />
              )}
              {reportMutation.isPending ? 'Submitting…' : 'Submit report'}
            </button>
            <button
              type="button"
              className="settings-action-secondary"
              onClick={() => {
                setReporting(false);
                setReportReason('');
                reportMutation.reset();
              }}
              disabled={reportMutation.isPending}
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      {reported && (
        <p
          className="settings-success contacts-report-success"
          role="status"
          aria-live="polite"
        >
          Report submitted. Our team will review within 3 business days. You
          won't hear back unless we need more details — if safety is urgent,
          block them too.
        </p>
      )}

      {(removeMutation.isError || toggleBlockMutation.isError) && (
        <p className="settings-error contacts-row-error" role="alert">
          {(
            (removeMutation.error ?? toggleBlockMutation.error) as unknown as
              | ApiError
              | undefined
          )?.message ?? 'Something went wrong.'}
        </p>
      )}
    </li>
  );
}
